import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "./UserContext";
import { useTranslation } from 'react-i18next';
import { changeLanguage } from '../i18n';
import { HiOutlineGlobeAlt } from 'react-icons/hi2';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useActivityTracker } from './useActivityTracker';
// useSiteTour artık FloatingHelpButton içinde global olarak kullanılıyor

const LANGS = [
  { code: 'TR', label: 'Türkçe' },
  { code: 'EN', label: 'English' },
  { code: 'DE', label: 'Deutsch' },
  { code: 'FR', label: 'Français' },
  { code: 'ES', label: 'Español' },
  { code: 'IT', label: 'Italiano' },
  { code: 'PT', label: 'Português' },
  { code: 'NL', label: 'Nederlands' },
  { code: 'AR', label: 'Arabic' },
];

// Renk paleti
const COLORS = {
  pending: '#f59e0b',
  approved: '#10b981',
  shipping: '#3b82f6',
  delivered: '#8b5cf6',
  cancelled: '#ef4444',
  paid: '#10b981',
  partial: '#f59e0b',
  unpaid: '#ef4444',
};

export default function TeklifDashboard({ onNewOffer }) {
  const navigate = useNavigate();
  const { user, login, logout, selectedFirma, selectFirma } = useUser();
  const { t } = useTranslation(); // eslint-disable-line no-unused-vars
  const { trackPageView, trackLogout, trackAction } = useActivityTracker();
  // Tur artık FloatingHelpButton tarafından yönetiliyor

  // Dashboard data state
  const [orderStats, setOrderStats] = useState(null);
  const [carilerStats, setCarilerStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Şifre değiştirme modal state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pwChangeNew, setPwChangeNew] = useState('');
  const [pwChangeConfirm, setPwChangeConfirm] = useState('');
  const [pwChangeError, setPwChangeError] = useState('');
  const [pwChangeLoading, setPwChangeLoading] = useState(false);

  // Navbar state
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);
  const [langDropdown, setLangDropdown] = useState(false);
  const [lang, setLang] = useState('TR');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    }
    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userDropdownOpen]);

  // ── Geçici şifre kontrolü (backend'den gerçek zamanlı) ──
  useEffect(() => {
    if (!user || !user.email) return;
    // Önce localStorage'daki flag'i kontrol et
    if (user.must_change_password) {
      setShowPasswordChange(true);
      return;
    }
    // Backend'den gerçek durumu kontrol et
    const checkTempPassword = async () => {
      try {
        const token = user.token;
        const resp = await fetch('/api/check-temp-password', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.must_change_password) {
            setShowPasswordChange(true);
          }
        }
      } catch (e) { /* sessiz hata */ }
    };
    checkTempPassword();
  }, [user]);

  const handlePasswordChange = async () => {
    setPwChangeError('');
    if (!pwChangeNew || pwChangeNew.length < 6) { setPwChangeError('Yeni şifre en az 6 karakter olmalıdır.'); return; }
    if (pwChangeNew !== pwChangeConfirm) { setPwChangeError('Şifreler eşleşmiyor.'); return; }
    setPwChangeLoading(true);
    try {
      const resp = await fetch('/api/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, current_password: user._temp_current || '', new_password: pwChangeNew })
      });
      const data = await resp.json();
      if (data.success) {
        // must_change_password flag'ini kaldır ve user'ı güncelle
        const updatedUser = { ...user, must_change_password: false };
        delete updatedUser.must_change_password;
        login(updatedUser);
        setShowPasswordChange(false);
        setPwChangeNew('');
        setPwChangeConfirm('');
      } else {
        setPwChangeError(data.error || 'Şifre değiştirilemedi.');
      }
    } catch {
      setPwChangeError('Sunucuya bağlanılamadı.');
    } finally {
      setPwChangeLoading(false);
    }
  };

  // Sayfa görüntüleme takibi
  useEffect(() => {
    trackPageView('dashboard');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Admin veri çekme ──
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    setLoading(true);
    Promise.all([
      fetch('/api/admin/orders/stats').then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/cariler/stats').then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/admin/orders?limit=5&page=1').then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([stats, cStats, ordersData]) => {
      setOrderStats(stats);
      setCarilerStats(cStats);
      setRecentOrders(ordersData?.orders || []);
    }).finally(() => setLoading(false));
  }, [user]);

  // ── Partner / Guest veri çekme ──
  useEffect(() => {
    if (!user || user.role === 'admin') return;
    setLoading(true);
    const email = user.email;
    const firmaId = selectedFirma?.id || user.firma_id;
    const summaryUrl = firmaId
      ? `/api/user/summary/${encodeURIComponent(email)}?firma_id=${firmaId}`
      : `/api/user/summary/${encodeURIComponent(email)}`;

    let ordersUrl;
    if (user.type === 'guest') {
      ordersUrl = firmaId
        ? `/api/guest-offers?email=${encodeURIComponent(email)}&firma_id=${firmaId}`
        : `/api/guest-offers?email=${encodeURIComponent(email)}`;
    } else {
      ordersUrl = `/api/user/orders/${encodeURIComponent(email)}${firmaId ? '?firma_id=' + firmaId : ''}`;
    }

    Promise.all([
      fetch(summaryUrl).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(ordersUrl).then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([summary, ordersData]) => {
      if (summary) {
        setOrderStats(summary);
      }
      const orders = Array.isArray(ordersData) ? ordersData : (ordersData?.orders || []);
      setRecentOrders(orders.slice(0, 5));
    }).finally(() => setLoading(false));
  }, [user, selectedFirma]);

  const displayName = user?.type === 'guest'
    ? (user?.firma || user?.email || 'Misafir')
    : (user?.name || user?.firma || 'Kullanıcı');

  // ── Durum Badge ──
  const StatusBadge = ({ status }) => {
    const map = {
      'pending': { bg: 'bg-amber-100 text-amber-800', label: 'Beklemede' },
      'approved': { bg: 'bg-emerald-100 text-emerald-800', label: 'Onaylandı' },
      'shipping': { bg: 'bg-blue-100 text-blue-800', label: 'Sevkiyatta' },
      'delivered': { bg: 'bg-purple-100 text-purple-800', label: 'Teslim Edildi' },
      'cancelled': { bg: 'bg-red-100 text-red-800', label: 'İptal' },
    };
    const info = map[status] || { bg: 'bg-gray-100 text-gray-700', label: status || '-' };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${info.bg}`}>{info.label}</span>;
  };

  // ── Empty State ──
  const EmptyState = ({ message, sub, buttonLabel }) => (
    <div className="text-center py-12">
      <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" /></svg>
      </div>
      <h3 className="text-sm font-semibold text-gray-700">{message}</h3>
      <p className="text-xs text-gray-400 mt-1 mb-4">{sub}</p>
      <button onClick={() => navigate('/teklif-olustur')}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#143b6b] text-white font-semibold text-sm rounded-xl hover:bg-[#0f2d52] transition shadow-sm">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
        {buttonLabel}
      </button>
    </div>
  );

  // ── Loading Skeleton ──
  const LoadingSkeleton = () => (
    <div className="animate-pulse space-y-6">
      <div className="h-32 bg-gray-200 rounded-2xl" />
      <div className="grid grid-cols-3 gap-4">
        <div className="h-24 bg-gray-200 rounded-2xl" />
        <div className="h-24 bg-gray-200 rounded-2xl" />
        <div className="h-24 bg-gray-200 rounded-2xl" />
      </div>
      <div className="h-64 bg-gray-200 rounded-2xl" />
    </div>
  );

  // ════════════════════════════════════════════════
  //  ADMIN DASHBOARD
  // ════════════════════════════════════════════════
  const AdminDashboard = () => {
    if (loading) return <LoadingSkeleton />;

    const stats = orderStats || { total: 0, statuses: [], payments: { paid: 0, partial: 0, unpaid: 0 } };
    const statusMap = {};
    (stats.statuses || []).forEach(s => { statusMap[s.status] = s.cnt; });

    const pending = statusMap['pending'] || 0;
    const approved = statusMap['approved'] || 0;
    const shipping = statusMap['shipping'] || 0;
    const delivered = statusMap['delivered'] || 0;
    const cancelled = statusMap['cancelled'] || 0;
    const totalCari = carilerStats?.total || 0;

    // Pie chart data
    const pieData = [
      { name: 'Beklemede', value: pending, color: COLORS.pending },
      { name: 'Onaylandı', value: approved, color: COLORS.approved },
      { name: 'Sevkiyatta', value: shipping, color: COLORS.shipping },
      { name: 'Teslim Edildi', value: delivered, color: COLORS.delivered },
      { name: 'İptal', value: cancelled, color: COLORS.cancelled },
    ].filter(d => d.value > 0);

    // Bar chart data
    const barData = [
      { name: 'Ödenmiş', value: stats.payments?.paid || 0, fill: COLORS.paid },
      { name: 'Kısmi', value: stats.payments?.partial || 0, fill: COLORS.partial },
      { name: 'Ödenmemiş', value: stats.payments?.unpaid || 0, fill: COLORS.unpaid },
    ];

    const statCards = [
      { label: 'Toplam Sipariş', value: stats.total || 0, color: 'blue', icon: 'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z', path: '/dash/siparis-yonetimi' },
      { label: 'Beklemede', value: pending, color: 'amber', icon: 'M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z', path: '/dash/siparis-yonetimi?status=pending' },
      { label: 'Onaylanan', value: approved, color: 'emerald', icon: 'M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z', path: '/dash/siparis-yonetimi?status=approved' },
      { label: 'Sevkiyatta', value: shipping, color: 'blue', icon: 'M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M3.75 12h16.5', path: '/dash/siparis-yonetimi?status=shipping' },
      { label: 'Teslim Edildi', value: delivered, color: 'violet', icon: 'M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z', path: '/dash/siparis-yonetimi?status=delivered' },
      { label: 'Toplam Cari', value: totalCari, color: 'cyan', icon: 'M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z', path: '/dash/cariler' },
    ];

    const quickAccess = [
      { label: 'Sipariş Oluştur', path: '/teklif-olustur', icon: 'M12 4.5v15m7.5-7.5h-15', color: 'emerald' },
      { label: 'Sipariş Yönetimi', path: '/dash/siparis-yonetimi', icon: 'M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z', color: 'blue' },
      { label: 'Cariler', path: '/dash/cariler', icon: 'M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z', color: 'violet' },
      { label: 'Fiyat Ayarları', path: '/dash/fiyat-ayarlari', icon: 'M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75', color: 'amber' },
      { label: 'Ürün Kataloğu', path: '/dash/sepetim', icon: 'M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z', color: 'cyan' },
      { label: 'Mail Gönderimi', path: '/admin/mailing', icon: 'M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75', color: 'rose' },
      { label: 'Aktivite Logları', path: '/dash/aktivite-loglar', icon: 'M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6', color: 'slate' },
      { label: 'Tur Yönetimi', path: '/dash/tur-yonetimi', icon: 'M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z', color: 'teal' },
      { label: 'Tur Yonetimi', path: '/dash/tur-yonetimi', icon: 'M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z', color: 'teal' },
      { label: 'Hesabım', path: '/dash/hesabim', icon: 'M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0', color: 'indigo' },
    ];

    return (
      <>
        {/* Hosgeldin Banneri */}
        <div className="bg-gradient-to-r from-[#143b6b] to-[#1e5199] rounded-2xl shadow-lg p-6 sm:p-8 mb-6 text-white relative overflow-hidden" data-tour="welcome-banner">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-blue-200 text-sm font-medium mb-1">Yönetim Paneli</p>
              <h1 className="text-2xl sm:text-3xl font-bold">Merhaba, {displayName}</h1>
              <p className="text-blue-200 mt-1 text-sm">Sisteminizin genel durumunu buradan takip edebilirsiniz.</p>
            </div>
            <button onClick={() => navigate('/teklif-olustur')}
              className="flex items-center gap-2 px-5 py-2.5 bg-white text-[#143b6b] rounded-xl font-semibold text-sm shadow-sm hover:bg-blue-50 transition hover:shadow-md flex-shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
              Yeni Sipariş
            </button>
          </div>
        </div>

        {/* Istatistik Kartlari */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-tour="stat-cards">
          {statCards.map((s, i) => (
            <div key={i} onClick={() => s.path && navigate(s.path)} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4 hover:shadow-md transition cursor-pointer active:scale-[0.97]">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0`}
                style={{ backgroundColor: s.color === 'blue' ? '#eff6ff' : s.color === 'amber' ? '#fffbeb' : s.color === 'emerald' ? '#ecfdf5' : s.color === 'violet' ? '#f5f3ff' : s.color === 'cyan' ? '#ecfeff' : '#eef2ff' }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"
                  style={{ color: s.color === 'blue' ? '#2563eb' : s.color === 'amber' ? '#d97706' : s.color === 'emerald' ? '#059669' : s.color === 'violet' ? '#7c3aed' : s.color === 'cyan' ? '#0891b2' : '#4f46e5' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={s.icon} />
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-500 font-medium">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Grafikler */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Pasta Grafik - Sipariş Durum Dağılımı */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Sipariş Durum Dağılımı</h3>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, name]} />
                  <Legend verticalAlign="bottom" height={36} formatter={(value) => <span className="text-xs text-gray-600">{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-400 text-sm">Henüz sipariş verisi yok</div>
            )}
          </div>

          {/* Bar Grafik - Ödeme Durumu */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Ödeme Durumu</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={barData} barSize={40}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip formatter={(value, name) => [value, 'Adet']} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {barData.map((entry, index) => (
                    <Cell key={`bar-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Hızlı Erişim */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6" data-tour="quick-access">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Hızlı Erişim</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {quickAccess.map((item, i) => (
              <button key={i} onClick={() => navigate(item.path)}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-100/80 transition hover:shadow-sm group">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm"
                  style={{ borderColor: item.color === 'emerald' ? '#d1fae5' : item.color === 'blue' ? '#dbeafe' : item.color === 'violet' ? '#ede9fe' : item.color === 'amber' ? '#fef3c7' : item.color === 'cyan' ? '#cffafe' : item.color === 'rose' ? '#fce7f3' : '#e0e7ff', borderWidth: 1 }}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"
                    style={{ color: item.color === 'emerald' ? '#059669' : item.color === 'blue' ? '#2563eb' : item.color === 'violet' ? '#7c3aed' : item.color === 'amber' ? '#d97706' : item.color === 'cyan' ? '#0891b2' : item.color === 'rose' ? '#e11d48' : '#4f46e5' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-gray-700 text-center">{item.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Son Siparişler Tablosu */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6" data-tour="recent-orders">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">Son Siparişler</h2>
            <button onClick={() => navigate('/dash/siparis-yonetimi')} className="text-sm text-blue-600 hover:text-blue-800 font-medium transition">
              Tümünü Gör &rarr;
            </button>
          </div>
          {recentOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-3 pr-4">Sipariş No</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-3 pr-4">Firma</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-3 pr-4">Tarih</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-3 pr-4">Durum</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-3">Tutar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {recentOrders.map(o => (
                    <tr key={o.id} className="hover:bg-blue-50/30 transition-colors cursor-pointer" onClick={() => navigate('/dash/siparis-yonetimi')}>
                      <td className="py-3.5 pr-4"><span className="font-semibold text-blue-800 text-sm">#{o.id}</span></td>
                      <td className="py-3.5 pr-4 text-sm text-gray-700">{o.firma || o.email || '-'}</td>
                      <td className="py-3.5 pr-4 text-sm text-gray-600">{o.created_at ? new Date(o.created_at).toLocaleDateString('tr-TR') : '-'}</td>
                      <td className="py-3.5 pr-4 flex items-center gap-2">
                        {o.siparis_tipi === 'devir' && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-indigo-100 text-indigo-700 border-indigo-200">Devir</span>}
                        <StatusBadge status={o.status} />
                      </td>
                      <td className="py-3.5 text-sm font-medium text-gray-800">{o.total ? `${Number(o.total).toLocaleString('tr-TR')} TL` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState message="Henüz sipariş bulunmuyor" sub="Yeni bir sipariş oluşturarak başlayın." buttonLabel="Yeni Sipariş" />
          )}
        </div>
      </>
    );
  };

  // ════════════════════════════════════════════════
  //  PARTNER / GUEST DASHBOARD
  // ════════════════════════════════════════════════
  const UserDashboard = () => {
    if (loading) return <LoadingSkeleton />;

    const isGuest = user?.type === 'guest';
    const stats = orderStats || { totalOrders: 0, pending: 0, approved: 0, shipping: 0, delivered: 0, cancelled: 0 };

    // Pie chart data
    const pieData = [
      { name: 'Beklemede', value: stats.pending || 0, color: COLORS.pending },
      { name: 'Onaylandı', value: stats.approved || 0, color: COLORS.approved },
      { name: 'Sevkiyatta', value: stats.shipping || 0, color: COLORS.shipping },
      { name: 'Teslim Edildi', value: stats.delivered || 0, color: COLORS.delivered },
      { name: 'İptal', value: stats.cancelled || 0, color: COLORS.cancelled },
    ].filter(d => d.value > 0);

    const statCards = [
      { label: 'Toplam Sipariş', value: stats.totalOrders || 0, color: '#2563eb', bg: '#eff6ff' },
      { label: 'Beklemede', value: stats.pending || 0, color: '#d97706', bg: '#fffbeb' },
      { label: 'Onaylanan', value: stats.approved || 0, color: '#059669', bg: '#ecfdf5' },
      { label: 'Teslim Edildi', value: stats.delivered || 0, color: '#7c3aed', bg: '#f5f3ff' },
    ];

    const gradientFrom = isGuest ? 'from-amber-500' : 'from-emerald-600';
    const gradientTo = isGuest ? 'to-orange-500' : 'to-teal-600';
    const subtitleColor = isGuest ? 'text-amber-100' : 'text-emerald-200';
    const panelLabel = isGuest ? 'Müşteri Paneli' : 'Bayi Paneli';
    const buttonTextColor = isGuest ? 'text-amber-700' : 'text-emerald-700';
    const buttonHoverBg = isGuest ? 'hover:bg-amber-50' : 'hover:bg-emerald-50';

    const quickAccess = [
      { label: 'Sipariş Oluştur', path: '/teklif-olustur', icon: 'M12 4.5v15m7.5-7.5h-15', color: '#059669', bg: '#ecfdf5', border: '#d1fae5' },
      { label: 'Siparişlerim', path: '/dash/siparislerim', icon: 'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z', color: '#2563eb', bg: '#eff6ff', border: '#dbeafe' },
      { label: 'Ödeme Detayları', path: '/dash/odeme', icon: 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z', color: '#d97706', bg: '#fffbeb', border: '#fef3c7' },
      { label: 'Hesabım', path: '/dash/hesabim', icon: 'M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0', color: '#7c3aed', bg: '#f5f3ff', border: '#ede9fe' },
    ];

    return (
      <>
        {/* Hosgeldin Banneri */}
        <div className={`bg-gradient-to-r ${gradientFrom} ${gradientTo} rounded-2xl shadow-lg p-6 sm:p-8 mb-6 text-white relative overflow-hidden`} data-tour="welcome-banner">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className={`${subtitleColor} text-sm font-medium`}>{panelLabel}</p>
                {isGuest && <span className="bg-white/20 text-white text-xs font-bold rounded-full px-3 py-0.5">Misafir</span>}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold">Merhaba, {displayName}</h1>
              <p className={`${subtitleColor} mt-1 text-sm`}>Siparişlerinizi buradan takip edebilir ve yeni sipariş oluşturabilirsiniz.</p>
            </div>
            <button onClick={() => navigate('/teklif-olustur')}
              className={`flex items-center gap-2 px-5 py-2.5 bg-white ${buttonTextColor} rounded-xl font-semibold text-sm shadow-sm ${buttonHoverBg} transition hover:shadow-md flex-shrink-0`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
              Sipariş Oluştur
            </button>
          </div>
        </div>

        {/* Istatistik Kartlari */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-tour="stat-cards">
          {statCards.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4 hover:shadow-md transition">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.bg }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ color: s.color }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={
                    i === 0 ? 'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z'
                    : i === 1 ? 'M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
                    : i === 2 ? 'M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
                    : 'M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z'
                  } />
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-500 font-medium">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Grafik + Hızlı Erişim Yan Yana */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Pasta Grafik */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Sipariş Durum Dağılımı</h3>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, name]} />
                  <Legend verticalAlign="bottom" height={36} formatter={(value) => <span className="text-xs text-gray-600">{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-400 text-sm">Henüz sipariş verisi yok</div>
            )}
          </div>

          {/* Hızlı Erişim */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-tour="quick-access">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Hızlı Erişim</h3>
            <div className="grid grid-cols-2 gap-3">
              {quickAccess.map((item, i) => (
                <button key={i} onClick={() => navigate(item.path)}
                  className="flex flex-col items-center gap-3 p-5 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-100/80 transition hover:shadow-sm group">
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm"
                    style={{ borderColor: item.border, borderWidth: 1 }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ color: item.color }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                    </svg>
                  </div>
                  <span className="text-xs font-semibold text-gray-700 text-center">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Son Siparişler Tablosu */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6" data-tour="recent-orders">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">Son Siparişlerim</h2>
            <button onClick={() => navigate('/dash/siparislerim')} className="text-sm text-blue-600 hover:text-blue-800 font-medium transition">
              Tümünü Gör &rarr;
            </button>
          </div>
          {recentOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-3 pr-4">Sipariş No</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-3 pr-4">Tarih</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-3 pr-4">Durum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {recentOrders.map(o => (
                    <tr key={o.id} className="hover:bg-blue-50/30 transition-colors cursor-pointer" onClick={() => navigate('/dash/siparislerim')}>
                      <td className="py-3.5 pr-4"><span className="font-semibold text-blue-800 text-sm">#{o.id}</span></td>
                      <td className="py-3.5 pr-4 text-sm text-gray-600">{o.created_at ? new Date(o.created_at).toLocaleDateString('tr-TR') : '-'}</td>
                      <td className="py-3.5 pr-4 flex items-center gap-2">
                        {o.siparis_tipi === 'devir' && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-indigo-100 text-indigo-700 border-indigo-200">Devir</span>}
                        <StatusBadge status={o.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState message="Henüz siparişiniz yok" sub="Yeni bir sipariş oluşturarak başlayın." buttonLabel="Sipariş Oluştur" />
          )}
        </div>
      </>
    );
  };

  // ════════════════════
  //  RENDER
  // ════════════════════
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-r from-[#e0e7ff] via-[#f7f8fa] to-[#c7d2fe] animate-gradient-move relative overflow-hidden w-full">
      <style>{`
        @keyframes gradient-move { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        .animate-gradient-move { background-size: 200% 200%; animation: gradient-move 12s ease-in-out infinite; }
        @keyframes fade-in { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
      `}</style>

      {/* ══════════════ NAVBAR ══════════════ */}
      <nav className="w-full bg-white px-0 py-0 shadow-sm border-b border-[#e3e6ef] flex items-center justify-between h-14 md:h-16 flex-shrink-0" data-tour="navbar">
        {/* Sol: Hamburger (mobil) + Logo */}
        <div className="flex items-center gap-2 h-full pl-3 md:pl-6">
          {user && (
            <button className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition" onClick={() => setMobileMenuOpen(v => !v)}>
              {mobileMenuOpen ? (
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              ) : (
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>
              )}
            </button>
          )}
          <div className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate('/dash')}>
            <img src="/invamedorder.svg" alt="INVAMED Order" className="h-7 md:h-10 w-auto" style={{objectFit:'contain'}} />
          </div>
        </div>

        {/* Sag: Dil (kisa) + Kullanici (desktop) */}
        <div className="flex items-center gap-2 md:gap-4 pr-3 md:pr-6 ml-auto">
          <div className="flex items-center gap-2">
            {/* Firma Secici */}
            {user?.firmalar && user.firmalar.length > 1 && selectedFirma && (
              <div className="relative hidden md:block">
                <button
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 transition text-amber-800 font-medium text-xs border border-amber-200"
                  onClick={() => { const el = document.getElementById('dashFirmaDropdown'); if (el) el.classList.toggle('hidden'); }}
                >
                  <svg className="w-3.5 h-3.5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 0h.008v.008h-.008V7.5Z" /></svg>
                  <span className="max-w-[100px] truncate">{selectedFirma.firma}</span>
                  <svg className="w-3 h-3 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                <div id="dashFirmaDropdown" className="hidden absolute right-0 mt-1 w-56 bg-white border border-amber-100 rounded-xl shadow-xl py-1 z-50">
                  <div className="px-3 py-1 text-[9px] font-bold text-amber-600 uppercase tracking-wider">Firma Değiştir</div>
                  {user.firmalar.map((f) => (
                    <button key={f.id} onClick={() => { selectFirma(f); document.getElementById('dashFirmaDropdown')?.classList.add('hidden'); }}
                      className={`w-full text-left px-3 py-1.5 flex items-center gap-2 transition text-xs ${selectedFirma?.id === f.id ? 'bg-amber-50 text-amber-900 font-semibold' : 'text-gray-700 hover:bg-amber-50'}`}>
                      <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[9px] font-bold ${selectedFirma?.id === f.id ? 'bg-amber-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{(f.firma || '?')[0].toUpperCase()}</span>
                      <span className="truncate">{f.firma}</span>
                      {selectedFirma?.id === f.id && <svg className="w-3 h-3 text-amber-600 ml-auto" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z" clipRule="evenodd" /></svg>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* User Dropdown - sadece desktop */}
            <div className="relative hidden md:block" ref={userDropdownRef}>
              {user && (
                <button className="flex items-center gap-2 px-3 py-1.5 border border-[#e3e6ef] text-[#143b6b] rounded-lg font-semibold bg-white hover:bg-[#e3e6ef] transition text-sm focus:outline-none" data-tour="user-menu"
                  onClick={() => setUserDropdownOpen(v => !v)}>
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-200 text-blue-900 font-bold text-base select-none">
                    {user.firma?.[0] || user.email?.[0] || user.name?.[0] || 'K'}
                  </span>
                  <span className="flex flex-col items-start">
                    <span className="text-blue-900 font-semibold text-xs">{user.firma || user.email}</span>
                    <span className="text-blue-700 text-xs">{user.name || ''}</span>
                  </span>
                  {user.type === 'guest' ? (
                    <span className="text-yellow-600 text-xs font-bold border border-yellow-400 rounded px-2 py-0.5 ml-2">Onaysız Profil</span>
                  ) : (
                    <span className="text-green-600 text-xs font-bold border border-green-400 rounded px-2 py-0.5 ml-2">Üye</span>
                  )}
                  <svg className={`w-4 h-4 ml-1 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
              )}
              {userDropdownOpen && user && (
                <div className="absolute right-0 mt-2 w-52 bg-white border border-blue-100 rounded-xl shadow-lg py-2 z-50 animate-fade-in">
                  <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/hesabim'); setUserDropdownOpen(false); }}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0" /></svg>
                    Hesabım
                  </button>
                  <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/sepetim'); setUserDropdownOpen(false); }}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" /></svg>
                    Sepetim
                  </button>
                  <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/siparislerim'); setUserDropdownOpen(false); }}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" /></svg>
                    Geçmiş Siparişlerim
                  </button>
                  <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/odeme'); setUserDropdownOpen(false); }}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" /></svg>
                    Ödeme Detayları
                  </button>
                  {user?.role === 'admin' && (
                    <>
                      <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/siparis-yonetimi'); setUserDropdownOpen(false); }}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z" /></svg>
                        Sipariş Yönetimi
                      </button>
                      <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/cariler'); setUserDropdownOpen(false); }}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" /></svg>
                        Cariler
                      </button>
                      <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/fiyat-ayarlari'); setUserDropdownOpen(false); }}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" /></svg>
                        Fiyat Ayarları
                      </button>
                      <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/admin/mailing'); setUserDropdownOpen(false); }}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg>
                        Mail Gönderimi
                      </button>
                      <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/aktivite-loglar'); setUserDropdownOpen(false); }}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" /></svg>
                        Aktivite Logları
                      </button>
                      <button className="w-full text-left px-4 py-2 text-blue-800 hover:bg-[#eaf7fe] hover:text-blue-900 transition cursor-pointer flex items-center gap-2" onClick={() => { navigate('/dash/tur-yonetimi'); setUserDropdownOpen(false); }}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z" /></svg>
                        Tur Yönetimi
                      </button>
                    </>
                  )}
                  <div className="border-t border-blue-100 my-1"></div>
                  <button className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 hover:text-red-700 transition cursor-pointer flex items-center gap-2" onClick={() => { trackLogout(); logout(); navigate('/login'); }}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" /></svg>
                    Çıkış Yap
                  </button>
                </div>
              )}
            </div>

            {/* Dil Secici - mobilde kisa kod */}
            <div className="relative">
              <button className="flex items-center gap-1 px-2 md:px-3 py-1.5 border border-[#e3e6ef] text-[#143b6b] rounded-lg font-semibold bg-white hover:bg-[#e3e6ef] transition text-sm"
                onClick={() => setLangDropdown(v => !v)}>
                <HiOutlineGlobeAlt className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden md:inline">{(LANGS.find(l => l.code === lang)?.label) || 'Türkçe'}</span>
                <span className="md:hidden text-xs font-bold">{lang}</span>
                <svg className={`w-3 h-3 transition-transform ${langDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </button>
              {langDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-[#e3e6ef] rounded-lg shadow-lg z-50 p-2">
                  <div className="grid grid-cols-3 gap-2">
                    {LANGS.map(l => (
                      <button key={l.code}
                        className={`flex items-center justify-center px-2 py-2 rounded-md text-[#143b6b] font-medium border border-transparent hover:bg-[#e3e6ef] transition text-sm ${lang === l.code ? 'bg-[#e3e6ef] border-[#143b6b] font-bold' : ''}`}
                        onClick={() => { setLang(l.code); changeLanguage(l.code); setLangDropdown(false); }}>
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {!user && (
            <button className="px-3 md:px-4 py-1.5 border border-[#143b6b] text-[#143b6b] rounded-lg font-semibold hover:bg-[#143b6b] hover:text-white transition text-xs md:text-sm"
              onClick={() => navigate('/teklif-olustur')}>Üye Ol</button>
          )}
        </div>
      </nav>

      {/* === MOBIL HAMBURGER MENU (full width) === */}
      {mobileMenuOpen && user && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setMobileMenuOpen(false)}>
          <div className="bg-white w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} style={{animation: 'slideDown 0.2s ease-out'}}>
            <style>{`@keyframes slideDown { from { transform: translateY(-100%); } to { transform: translateY(0); } }`}</style>
            {/* Kullanici bilgisi */}
            <div className="flex items-center gap-3 p-4 border-b border-gray-100 bg-blue-50/50">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-200 text-blue-900 font-bold text-lg select-none">
                {user.firma?.[0] || user.email?.[0] || 'K'}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-blue-900 truncate">{user.firma || user.email}</div>
                <div className="text-xs text-gray-500">{user.name || user.email}</div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {/* Menu ogeleri */}
            <div className="py-2">
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/teklif-olustur'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                Sipariş Oluştur
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/hesabim'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0" /></svg>
                Hesabım
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/sepetim'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" /></svg>
                Sepetim
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/siparislerim'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" /></svg>
                Siparişlerim
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/odeme'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" /></svg>
                Ödeme Detayları
              </button>
              {user?.role === 'admin' && (<>
              <div className="border-t border-gray-100 my-1 mx-4"></div>
              <div className="px-5 py-1.5 text-[10px] text-gray-400 font-bold uppercase tracking-wider">Yönetim</div>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/siparis-yonetimi'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z" /></svg>
                Sipariş Yönetimi
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/cariler'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" /></svg>
                Cariler
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/fiyat-ayarlari'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" /></svg>
                Fiyat Ayarları
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/admin/mailing'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg>
                Mail Gönderimi
              </button>
              <button className="w-full text-left px-5 py-3 text-sm text-gray-800 hover:bg-blue-50 transition flex items-center gap-3" onClick={() => { navigate('/dash/tur-yonetimi'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z" /></svg>
                Tur Yönetimi
              </button>
              </>)}
              <div className="border-t border-gray-100 my-1 mx-4"></div>
              <button className="w-full text-left px-5 py-3 text-sm text-red-600 hover:bg-red-50 transition flex items-center gap-3" onClick={() => { trackLogout(); logout(); navigate('/login'); setMobileMenuOpen(false); }}>
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" /></svg>
                Çıkış Yap
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════ MAIN CONTENT ══════════════ */}
      <div className="flex-1 w-full overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {user?.role === 'admin' ? <AdminDashboard /> : <UserDashboard />}
        </div>
      </div>

      {/* Tur butonu artık FloatingHelpButton içinde (App.js'de global) */}

      {/* ══════════════ ŞİFRE DEĞİŞTİRME MODALI ══════════════ */}
      {showPasswordChange && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{zIndex: 99999}}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-[#0d2b52] to-[#1e5199] px-6 py-5 text-white">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 rounded-full p-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" /></svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold">Şifre Değiştirme Zorunlu</h3>
                  <p className="text-blue-200 text-xs">Devam etmek için şifrenizi değiştirmeniz gerekmektedir</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" /></svg>
                <p className="text-amber-800 text-xs leading-relaxed">
                  Geçici şifre ile giriş yaptınız. <strong>3 gün içinde şifre değiştirilmezse hesabınız pasif moda alınacaktır.</strong>
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre</label>
                  <input type="password" value={pwChangeNew} onChange={e => setPwChangeNew(e.target.value)} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="En az 6 karakter" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre (Tekrar)</label>
                  <input type="password" value={pwChangeConfirm} onChange={e => setPwChangeConfirm(e.target.value)} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Şifreyi tekrar girin"
                    onKeyDown={e => { if (e.key === 'Enter') handlePasswordChange(); }}
                  />
                </div>
                {pwChangeError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-red-700 text-xs">{pwChangeError}</div>
                )}
                <button
                  onClick={handlePasswordChange}
                  disabled={pwChangeLoading}
                  className="w-full py-2.5 bg-gradient-to-r from-[#0d2b52] to-[#1e5199] text-white rounded-lg font-semibold text-sm hover:opacity-90 transition disabled:opacity-50"
                >
                  {pwChangeLoading ? 'Kaydediliyor...' : 'Şifreyi Değiştir ve Devam Et'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
